import { createContext, ReactNode, useContext, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

interface AuthContextType {
    user: any | null;
    order: SearchResult | null;
    changeOrder: (order : any ) => void;
    login: (user: any) => void;
    logout: () => void;
    newTabDetected: boolean;
    changeState: (state: boolean) => void;
}

export interface LineItem {
    id: string;
    description: string;
    subprice: string;
    unit_price_ttc: string;
    unit_price_ttc_net: string;
    qty: string;
    tva_tx: string;
    total_ht: string;
    total_tva: string;
    total_ttc: string;
    remise_percent: string;
    product_type: string;
    fk_product: string;
    brand_id: string;
    brand: string;
}

export interface Paiements {
    total: number;
    paiements: any[];
}

export interface SearchResult {
    id: string;
    ref: string;
    client: string;
    total_ht: string;
    total_ttc: string;
    date: string;
    status: string;
    type: number;
    paiements: Paiements;
    resteapayer: number;
    lines: LineItem[];
    invoicepdf: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};

interface Props {
    children: ReactNode;
}

const AuthProvider: React.FC<Props> = ({ children }) => {
    const dispatch = useDispatch(); // Add useDispatch
    const [user, setUser] = useState<any>(
        sessionStorage.getItem('authUser') ? JSON.parse(sessionStorage.getItem('authUser')!) : null
    );

    const [order, setOrder] = useState<SearchResult | null>(null)


    const [newTabDetected, setNewTabDetected] = useState(false);
    const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const INACTIVITY_LIMIT = 15 * 60 * 1000;
    const changeState = (newState: boolean) => {
        setNewTabDetected(newState);
    };

    const login = (userData: any) => {
        setUser(userData);
        sessionStorage.setItem('authUser', JSON.stringify(userData));
    };

    const changeOrder = (newOrder: any) => {
        setOrder(newOrder);
    };

    const logout = () => {
        setUser(null);
        sessionStorage.removeItem('authUser');
        changeOrder(null); // Clear selectedOrder on logout
    };

    const resetInactivityTimer = () => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }
        inactivityTimeoutRef.current = setTimeout(() => {
            if (user) {
                logout();
            }
        }, INACTIVITY_LIMIT);
    };
    const handleActivity = () => {
        resetInactivityTimer();
    };

    useEffect(() => {
        if (user) {
            resetInactivityTimer();
            const events = ['mousemove', 'mousedown', 'click', 'keydown'];
            events.forEach((event) => {
                window.addEventListener(event, handleActivity);
            });

            return () => {
                events.forEach((event) => {
                    window.removeEventListener(event, handleActivity);
                });
                if (inactivityTimeoutRef.current) {
                    clearTimeout(inactivityTimeoutRef.current);
                }
            };
        }
    }, [user]);

    return (
        <AuthContext.Provider value={{ user,order, login, logout, newTabDetected, changeState, changeOrder }}>
            {children}
        </AuthContext.Provider>
    );
};
export default AuthProvider;